import Phaser from 'phaser';
import { BEACH, HAUNTED, TOWN, type AreaLayout } from '@bb/shared';

/** Placeholder world art, drawn from the same layout numbers the server validates against. */

export const C = {
  grass: 0x8fcf7a,
  grassDark: 0x79bb66,
  plaza: 0xeedfc0,
  plazaLine: 0xdcc9a3,
  sand: 0xf1dfb2,
  sandDark: 0xe2cc95,
  water: 0x4fb3c4,
  waterDeep: 0x2f93a8,
  waterLight: 0x9fdde6,
  roofTeal: 0x16808f,
  roofCoral: 0xd4553e,
  roofPlum: 0x7a4f9a,
  wall: 0xfff6e6,
  wood: 0x9a6a43,
  woodLight: 0xc89a64,
  ink: 0x1d2b36,
  buoy: 0xe9a915,
};

export function labelStyle(size: number, color = '#1d2b36'): Phaser.Types.GameObjects.Text.TextStyle {
  return { fontFamily: '"Baloo 2", system-ui, sans-serif', fontSize: `${size}px`, fontStyle: '700', color };
}

/**
 * Draws the area's scenery, then bakes all vector shapes into one texture so
 * the renderer draws a single image per frame instead of re-tessellating
 * hundreds of shapes (matters a lot on low-end tablets).
 */
export function drawArea(scene: Phaser.Scene, area: AreaLayout, decor: string[] = [], liveEvents: string[] = []) {
  const before = new Set(scene.children.list);
  if (area.key === 'beach') drawBeach(scene);
  else if (area.key === 'haunted') drawHaunted(scene);
  else drawTown(scene);
  if (decor.includes('lanterns') && area.key !== 'haunted') drawLanterns(scene, area);
  if (decor.includes('halloween') && area.key !== 'haunted') drawHalloween(scene, area);
  if (area.key === 'town' && liveEvents.includes('haunted_harbor')) drawHauntedGate(scene);
  const key = `world:${area.key}:${decor.join(',')}:${liveEvents.includes('haunted_harbor') ? 'gate' : ''}`;
  const shapes = scene.children.list.filter((o): o is Phaser.GameObjects.Graphics => !before.has(o) && o instanceof Phaser.GameObjects.Graphics);
  if (!scene.textures.exists(key)) {
    const rt = scene.make.renderTexture({ width: area.width, height: area.height }, false);
    rt.draw(shapes);
    rt.saveTexture(key);
  }
  for (const g of shapes) g.destroy();
  scene.add.image(0, 0, key).setOrigin(0, 0).setDepth(0);
}

function signpost(scene: Phaser.Scene, x: number, y: number, title: string) {
  const g = scene.add.graphics().setDepth(4);
  g.fillStyle(C.wood).fillRect(x - 3, y - 6, 6, 26);
  g.fillStyle(C.woodLight).fillRoundedRect(x - 70, y - 34, 140, 32, 6);
  scene.add.text(x, y - 26, title, labelStyle(13)).setOrigin(0.5, 0).setDepth(5);
}

function drawTown(scene: Phaser.Scene) {
  const g = scene.add.graphics().setDepth(0);
  g.fillStyle(C.grass).fillRect(0, 0, TOWN.width, TOWN.height);
  g.fillStyle(C.grassDark);
  for (let i = 0; i < 70; i++) g.fillEllipse((i * 197) % TOWN.width, 240 + ((i * 89) % 560), 14, 5);
  g.fillStyle(C.water).fillRect(0, 772, TOWN.width, 28);
  g.fillStyle(C.waterLight);
  for (let x = 10; x < TOWN.width; x += 46) g.fillEllipse(x, 780, 22, 3);

  const w = TOWN.walkable[0]!;
  g.fillStyle(C.plaza).fillRoundedRect(w.x, w.y, w.w, w.h, 28);
  g.lineStyle(1, C.plazaLine, 0.9);
  for (let x = w.x + 40; x < w.x + w.w; x += 40) g.lineBetween(x, w.y + 6, x, w.y + w.h - 6);
  for (let y = w.y + 40; y < w.y + w.h; y += 40) g.lineBetween(w.x + 6, y, w.x + w.w - 6, y);

  shopFront(scene, g, { x: 80, y: 60, w: 300, h: 180 }, C.roofCoral, 'Tidewear Outfitters', 'Clothes · open now');
  shopFront(scene, g, { x: 900, y: 60, w: 300, h: 180 }, C.roofPlum, 'Bayside Arcade', 'Games · open now');

  // Lighthouse clock.
  const r = { x: 560, y: 20, w: 160, h: 220 };
  g.fillStyle(C.wall).fillRect(r.x + 40, r.y + 40, r.w - 80, r.h - 40);
  g.fillStyle(C.roofCoral);
  for (let y = r.y + 70; y < r.y + r.h - 20; y += 50) g.fillRect(r.x + 40, y, r.w - 80, 18);
  g.fillStyle(C.roofTeal).fillTriangle(r.x + 30, r.y + 44, r.x + r.w - 30, r.y + 44, r.x + r.w / 2, r.y);
  g.fillStyle(0xffffff).fillCircle(r.x + r.w / 2, r.y + 100, 22);
  g.lineStyle(3, C.ink).strokeCircle(r.x + r.w / 2, r.y + 100, 22);
  g.lineBetween(r.x + r.w / 2, r.y + 100, r.x + r.w / 2, r.y + 86);
  g.lineBetween(r.x + r.w / 2, r.y + 100, r.x + r.w / 2 + 10, r.y + 104);

  const f = TOWN.obstacles[0]!;
  const fx = f.x + f.w / 2;
  const fy = f.y + f.h / 2;
  g.fillStyle(0xbfae8f).fillEllipse(fx, fy + 6, f.w + 10, f.h);
  g.fillStyle(C.water).fillEllipse(fx, fy + 2, f.w - 14, f.h - 26);
  g.fillStyle(0xbfae8f).fillEllipse(fx, fy - 8, 34, 18);
  g.fillStyle(C.waterLight).fillEllipse(fx, fy - 20, 12, 22);

  const n = TOWN.obstacles[1]!;
  g.fillStyle(C.wood).fillRect(n.x + 6, n.y - 4, 6, n.h + 6).fillRect(n.x + n.w - 12, n.y - 4, 6, n.h + 6);
  g.fillStyle(C.woodLight).fillRoundedRect(n.x - 6, n.y - 46, n.w + 12, 44, 4);
  g.fillStyle(0xfff6e6).fillRect(n.x, n.y - 40, 22, 16).fillRect(n.x + 28, n.y - 38, 24, 20);
  scene.add.text(n.x + n.w / 2, n.y - 58, 'Notice Board', labelStyle(12)).setOrigin(0.5).setDepth(5);

  const l = TOWN.obstacles[2]!;
  g.fillStyle(C.ink).fillRect(l.x + l.w / 2 - 3, l.y - 60, 6, 90);
  g.fillStyle(C.buoy).fillCircle(l.x + l.w / 2, l.y - 66, 11);

  signpost(scene, 150, 742, '← Pebble Beach');
  signpost(scene, 1130, 742, 'Cove Cottages →');
}

function shopFront(
  scene: Phaser.Scene,
  g: Phaser.GameObjects.Graphics,
  r: { x: number; y: number; w: number; h: number },
  roof: number,
  name: string,
  sub: string,
) {
  g.fillStyle(C.wall).fillRect(r.x, r.y + 60, r.w, r.h - 60);
  g.fillStyle(roof).fillRoundedRect(r.x - 12, r.y + 14, r.w + 24, 60, 14);
  g.fillStyle(C.waterLight).fillRect(r.x + 26, r.y + 100, 60, 44).fillRect(r.x + r.w - 86, r.y + 100, 60, 44);
  g.fillStyle(C.wood).fillRoundedRect(r.x + r.w / 2 - 26, r.y + r.h - 70, 52, 70, { tl: 20, tr: 20, bl: 0, br: 0 });
  scene.add.text(r.x + r.w / 2, r.y + 44, name, labelStyle(17, '#ffffff')).setOrigin(0.5).setDepth(5);
  scene.add.text(r.x + r.w / 2, r.y + r.h + 4, sub, labelStyle(11, '#4f6676')).setOrigin(0.5, 0).setDepth(5);
}

function drawBeach(scene: Phaser.Scene) {
  const g = scene.add.graphics().setDepth(0);
  // Sea with gentle bands, sand below.
  g.fillStyle(C.waterDeep).fillRect(0, 0, BEACH.width, 200);
  g.fillStyle(C.water).fillRect(0, 200, BEACH.width, 220);
  g.fillStyle(C.waterLight);
  for (let i = 0; i < 40; i++) g.fillEllipse((i * 151) % BEACH.width, 40 + ((i * 67) % 340), 26, 3);
  g.fillStyle(C.sand).fillRect(0, 400, BEACH.width, 400);
  g.fillStyle(0xffffff, 0.7);
  for (let x = 0; x < BEACH.width; x += 30) g.fillEllipse(x + 15, 402, 30, 8);
  g.fillStyle(C.sandDark);
  for (let i = 0; i < 60; i++) g.fillEllipse((i * 173) % BEACH.width, 430 + ((i * 59) % 340), 6, 3);

  // Pier.
  const p = BEACH.walkable[1]!;
  g.fillStyle(C.wood).fillRect(p.x - 6, p.y - 10, p.w + 12, p.h + 10);
  g.fillStyle(C.woodLight);
  for (let y = p.y - 6; y < p.y + p.h; y += 14) g.fillRect(p.x - 4, y, p.w + 8, 10);
  g.fillStyle(C.wood);
  for (const y of [p.y + 20, p.y + 120, p.y + 220]) {
    g.fillRect(p.x - 12, y, 8, 18);
    g.fillRect(p.x + p.w + 4, y, 8, 18);
  }
  // Fishing spot marker.
  g.lineStyle(3, C.buoy).strokeCircle(640, 140, 26);
  g.fillStyle(C.buoy).fillCircle(700, 100, 7);
  g.fillStyle(0xffffff).fillCircle(700, 96, 3);
  scene.add.text(640, 90, 'Fishing spot', labelStyle(14, '#ffffff')).setOrigin(0.5).setDepth(5);

  // Rocks and umbrella.
  const rock = BEACH.obstacles[0]!;
  g.fillStyle(0x9aa4ab).fillEllipse(rock.x + 30, rock.y + 30, 60, 40).fillEllipse(rock.x + 62, rock.y + 34, 44, 30);
  g.fillStyle(0xb6c0c6).fillEllipse(rock.x + 26, rock.y + 24, 26, 12);
  const u = BEACH.obstacles[1]!;
  g.fillStyle(C.ink).fillRect(u.x + u.w / 2 - 2, u.y - 10, 4, u.h + 10);
  g.fillStyle(C.roofCoral).fillEllipse(u.x + u.w / 2, u.y - 10, 110, 40);
  g.fillStyle(0xffffff).fillEllipse(u.x + u.w / 2, u.y - 10, 36, 40);
  g.fillStyle(0xf6d7c3).fillRoundedRect(u.x - 50, u.y + 40, 80, 30, 6);

  // Cliffs with the Deep Mine entrance: a timber-framed tunnel and a lantern.
  const m = BEACH.obstacles[2]!;
  g.fillStyle(0x7d6a5a).fillTriangle(m.x - 30, m.y + m.h, m.x + 60, m.y - 90, m.x + 120, m.y + m.h);
  g.fillStyle(0x8b7866).fillTriangle(m.x + 50, m.y + m.h, m.x + 140, m.y - 70, BEACH.width, m.y + m.h);
  g.fillStyle(0x6b5a4c).fillRect(m.x - 20, m.y - 10, m.w + 60, m.h + 10);
  g.fillStyle(0x9a8775);
  for (const [x, y, r] of [[m.x + 10, m.y + 20, 14], [m.x + 150, m.y + 10, 18], [m.x + 40, m.y - 20, 10], [m.x + 120, m.y + 60, 12]]) g.fillCircle(x!, y!, r!);
  const ex = m.x + 80;
  g.fillStyle(0x1b1410).fillRoundedRect(ex - 34, m.y + 18, 68, 72, { tl: 30, tr: 30, bl: 0, br: 0 });
  g.fillStyle(C.wood).fillRect(ex - 42, m.y + 14, 10, 76).fillRect(ex + 32, m.y + 14, 10, 76).fillRect(ex - 46, m.y + 8, 92, 12);
  g.fillStyle(0x5a5a5a).fillRect(ex - 30, m.y + 86, 60, 4);
  g.fillStyle(C.buoy).fillCircle(ex + 52, m.y + 40, 6);
  g.fillStyle(0xfff3c4, 0.35).fillCircle(ex + 52, m.y + 40, 14);
  scene.add.text(ex, m.y - 6, 'Deep Mine', labelStyle(14, '#ffffff')).setOrigin(0.5).setDepth(5);

  signpost(scene, 1130, 742, 'Town Square →');
}

/** Harvest Lantern Festival decorations: strings of lanterns and pumpkins. */
function drawLanterns(scene: Phaser.Scene, area: AreaLayout) {
  const g = scene.add.graphics().setDepth(3);
  const colors = [0xe9a915, 0xe8812c, 0xd4553e, 0xf3c24d];
  const strings = area.key === 'beach' ? [[40, 380, 560, 380], [720, 380, 1040, 380]] : [[60, 262, 1220, 262], [60, 760, 1220, 760]];
  for (const [x1, y1, x2] of strings) {
    g.lineStyle(2, 0x5a3a22, 0.8);
    const n = 18;
    for (let i = 0; i < n; i++) {
      const xa = x1! + ((x2! - x1!) * i) / n;
      const xb = x1! + ((x2! - x1!) * (i + 1)) / n;
      g.lineBetween(xa, y1! + Math.sin((i / n) * Math.PI * 6) * 4, xb, y1! + Math.sin(((i + 1) / n) * Math.PI * 6) * 4);
      g.fillStyle(colors[i % colors.length]!, 1).fillEllipse(xb, y1! + 10, 12, 16);
      g.fillStyle(0xfff3c4, 0.35).fillCircle(xb, y1! + 10, 12);
    }
  }
  const pumpkins = area.key === 'beach' ? [[200, 600], [1050, 680]] : [[560, 500], [720, 500], [120, 300], [1160, 300]];
  for (const [x, y] of pumpkins) {
    for (const dx of [-8, 8, 0]) g.fillStyle(dx === 0 ? 0xe8812c : 0xc96a1e, 1).fillEllipse(x! + dx, y!, 16, 20);
    g.fillStyle(0x4f7a2a, 1).fillRect(x! - 2, y! - 15, 4, 6);
  }
}

// ---------------------------------------------------------------------------
// Haunted Harbor Halloween
// ---------------------------------------------------------------------------

const H = { night: 0x1f1a33, wall: 0x2e2547, wallLine: 0x3a2f5a, floor: 0x3b2f2a, board: 0x4a3a32, purple: 0x9b6ad6, orange: 0xf08a24, bone: 0xf3e7b4, ghost: 0xf4f6f8 };

function jackOLantern(g: Phaser.GameObjects.Graphics, x: number, y: number, s = 1) {
  for (const dx of [-8, 8, 0]) g.fillStyle(dx === 0 ? H.orange : 0xc96a1e, 1).fillEllipse(x + dx * s, y, 18 * s, 22 * s);
  g.fillStyle(0x4f7a2a, 1).fillRect(x - 2 * s, y - 16 * s, 4 * s, 6 * s);
  g.fillStyle(0xfff3a0, 1).fillTriangle(x - 7 * s, y - 2 * s, x - 3 * s, y - 2 * s, x - 5 * s, y - 7 * s);
  g.fillTriangle(x + 3 * s, y - 2 * s, x + 7 * s, y - 2 * s, x + 5 * s, y - 7 * s);
  g.fillRect(x - 6 * s, y + 3 * s, 12 * s, 3 * s);
}

function bat(g: Phaser.GameObjects.Graphics, x: number, y: number) {
  g.fillStyle(0x1d1d2b, 1).fillEllipse(x, y, 10, 8);
  g.fillTriangle(x - 4, y, x - 20, y - 6, x - 12, y + 4).fillTriangle(x + 4, y, x + 20, y - 6, x + 12, y + 4);
}

/** Bats, fog and grinning pumpkins over the town and beach while Halloween runs. */
function drawHalloween(scene: Phaser.Scene, area: AreaLayout) {
  const g = scene.add.graphics().setDepth(3);
  g.fillStyle(H.night, 0.18).fillRect(0, 0, area.width, area.height);
  for (let i = 0; i < 9; i++) bat(g, 80 + ((i * 151) % (area.width - 160)), 30 + ((i * 47) % 140));
  g.fillStyle(0xffffff, 0.08);
  for (let i = 0; i < 8; i++) g.fillEllipse((i * 197) % area.width, area.height - 40 - (i % 3) * 20, 260, 30);
  const spots = area.key === 'beach' ? [[160, 470], [470, 700], [880, 470], [1180, 620]] : [[80, 300], [1200, 300], [470, 720], [810, 720], [440, 400], [840, 400]];
  for (const [x, y] of spots) jackOLantern(g, x!, y!);
}

/** The gate to the Haunted House at the bottom of Town Square (event only). */
function drawHauntedGate(scene: Phaser.Scene) {
  const g = scene.add.graphics().setDepth(4);
  const x = 640;
  const y = 700;
  g.fillStyle(0x2a2238, 1).fillRect(x - 74, y - 40, 10, 70).fillRect(x + 64, y - 40, 10, 70);
  g.lineStyle(5, 0x2a2238, 1);
  g.beginPath();
  g.arc(x, y - 40, 69, Math.PI, 0);
  g.strokePath();
  g.lineStyle(2, 0x2a2238, 1);
  for (let i = -3; i <= 3; i++) g.lineBetween(x + i * 18, y - 30, x + i * 18, y + 28);
  jackOLantern(g, x - 69, y - 54, 0.9);
  jackOLantern(g, x + 69, y - 54, 0.9);
  scene.add.text(x, y - 124, 'Haunted House', labelStyle(16, '#f08a24')).setOrigin(0.5).setDepth(5);
  scene.add.text(x, y - 104, 'Halloween only!', labelStyle(11, '#4f6676')).setOrigin(0.5).setDepth(5);
}

/** Inside the Haunted House: a moonlit hall with two shops, the spooky arcade and plenty of hiding spots. */
function drawHaunted(scene: Phaser.Scene) {
  const g = scene.add.graphics().setDepth(0);
  g.fillStyle(H.night).fillRect(0, 0, HAUNTED.width, HAUNTED.height);
  // Back wall with wallpaper stripes.
  g.fillStyle(H.wall).fillRect(0, 0, HAUNTED.width, 300);
  g.fillStyle(H.wallLine);
  for (let x = 0; x < HAUNTED.width; x += 48) g.fillRect(x, 0, 6, 300);
  // Floorboards.
  const f = HAUNTED.walkable[0]!;
  g.fillStyle(H.floor).fillRect(0, 290, HAUNTED.width, HAUNTED.height - 290);
  g.lineStyle(2, H.board, 1);
  for (let y = 320; y < HAUNTED.height; y += 34) g.lineBetween(0, y, HAUNTED.width, y);
  g.fillStyle(0x5a2a3a, 0.9).fillRoundedRect(f.x + 140, f.y + 60, f.w - 280, f.h - 140, 30); // long rug
  g.lineStyle(3, H.orange, 0.5).strokeRoundedRect(f.x + 154, f.y + 74, f.w - 308, f.h - 168, 24);

  // Windows with a full moon.
  for (const wx of [400, 880]) {
    g.fillStyle(0x0f0c1d).fillRoundedRect(wx - 40, 40, 80, 110, { tl: 40, tr: 40, bl: 4, br: 4 });
    g.fillStyle(H.bone).fillCircle(wx + 8, 80, 16);
    g.lineStyle(4, 0x1d1d2b).lineBetween(wx, 40, wx, 150).lineBetween(wx - 40, 100, wx + 40, 100);
  }
  // Cobwebs in the corners.
  g.lineStyle(1, 0xd8d8e8, 0.55);
  for (const [cx, sx] of [[0, 1], [HAUNTED.width, -1]] as const) {
    for (let i = 1; i <= 4; i++) g.lineBetween(cx, 0, cx + sx * 90, i * 22);
    for (let r = 20; r <= 80; r += 20) {
      g.beginPath();
      g.arc(cx, 0, r, sx > 0 ? 0 : Math.PI / 2, sx > 0 ? Math.PI / 2 : Math.PI);
      g.strokePath();
    }
  }

  const front = (r: { x: number; y: number; w: number; h: number }, color: number, title: string, sub: string) => {
    g.fillStyle(0x3a2f5a).fillRect(r.x, r.y + 50, r.w, r.h - 50);
    g.fillStyle(color).fillRoundedRect(r.x - 10, r.y + 10, r.w + 20, 54, 12);
    g.fillStyle(0x1d1d2b).fillRoundedRect(r.x + r.w / 2 - 28, r.y + r.h - 78, 56, 78, { tl: 26, tr: 26, bl: 0, br: 0 });
    g.fillStyle(0xfff3a0, 0.85).fillRect(r.x + 24, r.y + 92, 52, 40).fillRect(r.x + r.w - 76, r.y + 92, 52, 40);
    scene.add.text(r.x + r.w / 2, r.y + 37, title, labelStyle(17, '#ffffff')).setOrigin(0.5).setDepth(5);
    scene.add.text(r.x + r.w / 2, r.y + r.h + 4, sub, labelStyle(11, '#d8cfee')).setOrigin(0.5, 0).setDepth(5);
  };
  const [boo, games, patch] = HAUNTED.hotspots;
  front(boo!.rect, H.purple, 'The Boo-tique', 'Costumes · pay with candy');
  front(games!.rect, 0x7a2f3a, 'Spooky Games', 'Ghost Pop · Spooky Trivia');
  front(patch!.rect, H.orange, 'Pumpkin Patch Market', 'Spooky decor · pay with candy');

  // Séance table, coffin and grandfather clock (the obstacles).
  const [table, coffin, clock] = HAUNTED.obstacles;
  g.fillStyle(0x2a1f1a).fillEllipse(table!.x + table!.w / 2, table!.y + table!.h / 2, table!.w + 20, table!.h);
  g.fillStyle(0x7a2f3a).fillEllipse(table!.x + table!.w / 2, table!.y + table!.h / 2 - 6, table!.w, table!.h - 18);
  g.fillStyle(0xb7e3ff, 0.8).fillCircle(table!.x + table!.w / 2, table!.y + 14, 14); // crystal ball
  g.fillStyle(0x4a3326).fillRoundedRect(coffin!.x, coffin!.y, coffin!.w, coffin!.h, 10);
  g.lineStyle(3, H.bone, 0.7).lineBetween(coffin!.x + 30, coffin!.y + coffin!.h / 2, coffin!.x + coffin!.w - 30, coffin!.y + coffin!.h / 2);
  g.fillStyle(0x4a3326).fillRect(clock!.x, clock!.y - 60, clock!.w, clock!.h + 60);
  g.fillStyle(H.bone).fillCircle(clock!.x + clock!.w / 2, clock!.y - 30, 18);
  g.lineStyle(2, 0x1d1d2b).lineBetween(clock!.x + clock!.w / 2, clock!.y - 30, clock!.x + clock!.w / 2, clock!.y - 44);

  // Candles and pumpkins.
  for (const [x, y] of [[90, 760], [1190, 760], [520, 330], [760, 330]] as const) jackOLantern(g, x, y);
  for (const [x, y] of [[250, 330], [1030, 330]] as const) {
    g.fillStyle(H.bone).fillRect(x - 4, y - 18, 8, 22);
    g.fillStyle(0xffd27a, 0.9).fillEllipse(x, y - 24, 8, 12);
    g.fillStyle(0xffd27a, 0.2).fillCircle(x, y - 22, 22);
  }
  signpost(scene, 640, 742, '← Town Square');
}
